import { api } from '../lib/api';
import type { BootstrapResponse, WorkspaceSummary } from '../types/workspace';
import type { CategoryCollection, CategoryField, EntityCategory, EntityDocument, EntitySummary, EntityTemplate, ExplorerNode, ExplorerSortConfig } from '../types/entities';

type WorkspaceConfigurationStore = {
  workspace: WorkspaceSummary | null;
  categories: EntityCategory[];
  categoryFields: CategoryField[];
  templates: EntityTemplate[];
  categoryCollections: CategoryCollection[];
  entities: EntitySummary[];
  explorerNodes: ExplorerNode[];
  activeEntity: EntityDocument | null;
  refreshConfiguration(): Promise<void>;
  loadEntities(): Promise<void>;
  loadExplorer(): Promise<void>;
  loadCategoryCollections(): Promise<void>;
  loadBacklinks(entityId?: string): Promise<void>;
  openEntity(id: string, updateHistory?: boolean, force?: boolean, requireClean?: boolean): Promise<EntityDocument | null | undefined>;
};

export const workspaceConfigurationActions = {
  async refreshConfiguration(this: WorkspaceConfigurationStore) {
    if (!this.workspace) return;
    const bootstrap = await api<BootstrapResponse>(`/api/v1/bootstrap?workspaceId=${encodeURIComponent(this.workspace.id)}`);
    this.workspace = bootstrap.workspace;
    this.templates = bootstrap.templates;
    this.categories = bootstrap.categories;
    this.categoryCollections = bootstrap.categoryCollections;
    this.categoryFields = bootstrap.categoryFields;
  },
  async createCategory(this: WorkspaceConfigurationStore, input: { key: string; name: string; icon: string | null; color: string; showInExplorer?: boolean }) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/categories`, {
      method: 'POST', body: JSON.stringify(input)
    });
    await this.refreshConfiguration();
  },
  async createTemplate(this: WorkspaceConfigurationStore, input: {
    key: string; name: string; category: string; icon: string | null;
    parentTemplateId?: string | null; locationParentTemplateId?: string | null; locationParentSourceFieldKey?: string; fields: Array<Record<string, unknown>>; aiConfig?: Record<string, unknown>; showInExplorer?: boolean; isGroup?: boolean; explorerSort?: ExplorerSortConfig | null; profileFieldKeys?: string[] | null; developmentKind?: 'goal' | 'attribute' | 'relationship' | null;
  }) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/templates`, {
      method: 'POST', body: JSON.stringify(input)
    });
    await this.refreshConfiguration();
  },
  async updateCategory(this: WorkspaceConfigurationStore, categoryId: string, input: { name?: string; icon?: string | null; color?: string; showInExplorer?: boolean; sortOrder?: number; status?: 'active' | 'archived' }) {
    const updated = await api<EntityCategory>(`/api/v1/categories/${categoryId}`, { method: 'PATCH', body: JSON.stringify(input) });
    if (input.showInExplorer !== undefined && updated.showInExplorer !== input.showInExplorer) {
      throw new Error('Die Explorer-Sichtbarkeit konnte nicht bestätigt werden.');
    }
    this.categories = this.categories.map((category) => category.id === categoryId ? updated : category);
    await this.refreshConfiguration();
    return updated;
  },
  async moveCategory(this: WorkspaceConfigurationStore, categoryId: string, direction: -1 | 1) {
    const ordered = [...this.categories].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    const index = ordered.findIndex((item) => item.id === categoryId); const other = ordered[index + direction]; const current = ordered[index];
    if (!current || !other) return;
    await api(`/api/v1/categories/${current.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: other.sortOrder }) });
    await api(`/api/v1/categories/${other.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: current.sortOrder }) });
    await this.refreshConfiguration();
  },
  async deleteCategory(this: WorkspaceConfigurationStore, categoryId: string, input: { mode: 'move' | 'delete'; targetCategoryId?: string }) {
    await api(`/api/v1/categories/${categoryId}`, { method: 'DELETE', body: JSON.stringify(input) });
    await this.refreshConfiguration();
    await this.loadEntities();
    await this.loadExplorer();
    if (this.activeEntity && !this.entities.some((entity) => entity.id === this.activeEntity?.id)) this.activeEntity = null;
  },
  async updateTemplate(this: WorkspaceConfigurationStore, templateId: string, input: {
    name?: string; category?: string; parentTemplateId?: string | null; locationParentTemplateId?: string | null; locationParentSourceFieldKey?: string; icon?: string | null; fields?: Array<object>;
    aiConfig?: Record<string, unknown>; showInExplorer?: boolean; isGroup?: boolean; explorerSort?: ExplorerSortConfig | null; profileFieldKeys?: string[] | null; developmentKind?: 'goal' | 'attribute' | 'relationship' | null; status?: 'active' | 'retired'; fieldRemovalMode?: 'remove-values'; sortOrder?: number;
  }) {
    await api(`/api/v1/templates/${templateId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.refreshConfiguration();
    await this.loadEntities();
    if (this.activeEntity?.templateId === templateId) await this.openEntity(this.activeEntity.id, false, true);
  },
  async changeEntityTemplate(this: WorkspaceConfigurationStore, entityId: string, templateId: string) {
    const entity = await api<EntityDocument>(`/api/v1/entities/${entityId}/template`, { method: 'PATCH', body: JSON.stringify({ templateId }) });
    if (this.activeEntity?.id === entityId) this.activeEntity = entity;
    await Promise.all([this.loadEntities(), this.loadExplorer(), this.loadBacklinks(entityId)]);
    return entity;
  },
  async moveTemplate(this: WorkspaceConfigurationStore, templateId: string, direction: -1 | 1) {
    const ordered = [...this.templates].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    const index = ordered.findIndex((item) => item.id === templateId); const other = ordered[index + direction]; const current = ordered[index];
    if (!current || !other) return;
    await api(`/api/v1/templates/${current.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: other.sortOrder }) });
    await api(`/api/v1/templates/${other.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: current.sortOrder }) });
    await this.refreshConfiguration();
  },
  async deleteTemplate(this: WorkspaceConfigurationStore, templateId: string, input: { mode: 'move' | 'delete'; targetTemplateId?: string }) {
    await api(`/api/v1/templates/${templateId}`, { method: 'DELETE', body: JSON.stringify(input) });
    await this.refreshConfiguration();
    await this.loadEntities();
    await this.loadExplorer();
    if (this.activeEntity && !this.entities.some((entity) => entity.id === this.activeEntity?.id)) this.activeEntity = null;
  },
  async createFolder(this: WorkspaceConfigurationStore, label: string, parentId: string | null) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/explorer-nodes`, { method: 'POST', body: JSON.stringify({ kind: 'folder', label, parentId }) });
    await this.loadExplorer();
  },
  async createCategoryCollection(this: WorkspaceConfigurationStore, input: { categoryKey: string; templateId?: string | null; name: string; parentId?: string | null }) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/category-collections`, { method: 'POST', body: JSON.stringify(input) });
    await this.loadCategoryCollections();
  },
  async updateCategoryCollection(this: WorkspaceConfigurationStore, id: string, input: { name?: string; templateId?: string | null; parentId?: string | null; sortOrder?: number }) {
    await api(`/api/v1/category-collections/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.loadCategoryCollections();
  },
  async deleteCategoryCollection(this: WorkspaceConfigurationStore, id: string, input: { mode: 'unassign' | 'move'; targetCollectionId?: string }) {
    await api(`/api/v1/category-collections/${id}`, { method: 'DELETE', body: JSON.stringify(input) });
    await this.loadCategoryCollections();
  },
  async createCategoryField(this: WorkspaceConfigurationStore, categoryKey: string, input: { key: string; label: string; kind: string; required?: boolean; multiple?: boolean; trackable?: boolean; validation?: Record<string, unknown>; allowedTemplateCategories?: string[]; allowedTemplateKeys?: string[] }) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/categories/${categoryKey}/fields`, { method: 'POST', body: JSON.stringify(input) });
    await this.refreshConfiguration();
  },
  async updateCategoryField(this: WorkspaceConfigurationStore, fieldId: string, input: { trackable: boolean }) { await api(`/api/v1/category-fields/${fieldId}`, { method: 'PATCH', body: JSON.stringify(input) }); await this.refreshConfiguration(); },
  async deleteCategoryField(this: WorkspaceConfigurationStore, fieldId: string) { await api(`/api/v1/category-fields/${fieldId}`, { method: 'DELETE' }); await this.refreshConfiguration(); },
  async addEntityToCategoryCollection(this: WorkspaceConfigurationStore, collectionId: string, entityId: string) {
    await api(`/api/v1/category-collections/${collectionId}/entities/${entityId}`, { method: 'POST' });
    await this.loadCategoryCollections();
  },
  async removeEntityFromCategoryCollection(this: WorkspaceConfigurationStore, collectionId: string, entityId: string) {
    await api(`/api/v1/category-collections/${collectionId}/entities/${entityId}`, { method: 'DELETE' });
    await this.loadCategoryCollections();
  },
  async addEntityToExplorer(this: WorkspaceConfigurationStore, entityId: string, parentId: string | null) {
    if (!this.workspace) return;
    await api(`/api/v1/workspaces/${this.workspace.id}/explorer-nodes`, { method: 'POST', body: JSON.stringify({ kind: 'entity', targetEntityId: entityId, parentId }) });
    await this.loadExplorer();
  },
  async updateExplorerNode(this: WorkspaceConfigurationStore, nodeId: string, input: { label?: string; parentId?: string | null }) {
    await api(`/api/v1/explorer-nodes/${nodeId}`, { method: 'PATCH', body: JSON.stringify(input) });
    await this.loadExplorer();
  },
  async deleteExplorerNode(this: WorkspaceConfigurationStore, nodeId: string) {
    await api(`/api/v1/explorer-nodes/${nodeId}`, { method: 'DELETE' });
    await this.loadExplorer();
  },
};
